import { Box, Card, CardContent, CircularProgress, Divider, Grid, Link } from '@material-ui/core'
import EmailIcon from '@material-ui/icons/Email'
import PhoneIcon from '@material-ui/icons/Phone'
import LockIcon from '@material-ui/icons/Lock'
import type { User } from '@paintscout/api'
import { getHighestRole } from '@paintscout/util'
import {
  Button,
  ConfirmationDialog,
  FloatingMenu,
  FormSection,
  FormSectionTitle,
  FormikCheckbox,
  FormikInputField,
  MenuItem,
  Select,
  Typography,
  useDialogs
} from '@paintscout/ui'
import { DeactivateUserDialog } from '@paintscout/react-quote'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import { Field, FieldArray, useFormikContext } from 'formik'
import type { OptionsDocument, UserPreferencesDocument } from 'paintscout'
import { default as React, useEffect, useState } from 'react'
import { useAuth } from 'react-auth0'
import { useSnackbar } from 'notistack'
import UserImportExport from './UserImportExport'

const GET_MFA_ENROLLMENTS = gql`
  query getMfaEnrollments($userId: String!) {
    getMfaEnrollments(userId: $userId) {
      id
      type
      name
      phone_number
      email
      enrolled_at
    }
  }
`

const RESET_MFA_FOR_USER = gql`
  mutation resetMfaForUser($userId: String!) {
    resetMfaForUser(userId: $userId)
  }
`

export interface UserAdministrationFormProps {
  section?: string

  onSectionChange?: (section: string) => any
}

export default function UserAdministrationForm({ section = 'general', onSectionChange }: UserAdministrationFormProps) {
  const [menuValue, setMenuValue] = useState(section)

  // update if url changes
  useEffect(() => {
    setMenuValue(section)
  }, [section])

  function handleMenuItemClick(item) {
    if (onSectionChange) {
      onSectionChange(item.key)
    }
    setMenuValue(item.key)
  }

  const menuItems = [
    { key: 'general', label: 'General' },
    { key: 'gamification', label: 'Gamification' },
    { key: 'importExport', label: 'Import/Export' }
  ]

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} xl={2}>
        <FloatingMenu
          value={menuValue}
          defaultValue="general"
          items={menuItems}
          onItemClick={handleMenuItemClick}
          collapseOn="lg"
        />
      </Grid>
      <Grid item xs={12} xl={10}>
        <Card>
          <CardContent>
            {menuValue === 'general' && <GeneralForm />}
            {menuValue === 'gamification' && <GamificationForm />}
            {menuValue === 'importExport' && <UserImportExport />}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
}

function GamificationForm() {
  const { values } = useFormikContext<{
    options: OptionsDocument
    preferences: UserPreferencesDocument
    user: User
  }>()

  console.log({ values })

  return (
    <>
      <FormSectionTitle title="Leaderboards" />
      <FormSection>
        <Grid item>
          <Field
            component={FormikCheckbox}
            name="user.user_metadata.leaderboardOptOut"
            label={`Remove completely from leaderboards`}
            toolTip={'The user will not see or be included in any leaderboards.'}
          />
          <Typography variant="body2">
            Users can be excluded from the leaderboard with this checkbox. They will still see gamification
            functionality & leaderboards, but their stats will not be ranked. Typically this would be used for "banning"
            users from the leaderboards for various reasons.
          </Typography>
        </Grid>
      </FormSection>
    </>
  )
}

function GeneralForm() {
  const { openDialog, dismissDialog } = useDialogs()
  const { values, setFieldValue, submitForm } = useFormikContext<{
    options: OptionsDocument
    preferences: UserPreferencesDocument
    user: User
  }>()
  const { session } = useAuth()
  const isSuperadmin = session?.app_metadata.roles.includes('superadmin')
  const isViewingSelf = session?.user_id === values.user.user_id

  console.log({ values, isSuperadmin, isViewingSelf })

  return (
    <>
      <FormSectionTitle title="General" />
      <FormSection>
        <Grid container spacing={2} style={{ paddingBottom: '5px' }}>
          <Grid item xs={12}>
            <Typography variant="body1">
              User is currently: {values.user.app_metadata.active ? 'Active' : 'Inactive'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Button
              onClick={() => {
                if (values.user.app_metadata.active) {
                  openDialog(ConfirmationDialog, {
                    title: 'Deactivate User',
                    message:
                      'Are you sure you want to deactivate this user? Clients chargebee subscription will be updated to exclue user if they are a billing client.',
                    scary: true,
                    onCancel: dismissDialog,
                    onConfirm: () => {
                      dismissDialog()
                      openDialog(DeactivateUserDialog, {
                        user: values.user,
                        onClose: dismissDialog,
                        onConfirm: () => {
                          window.location.reload()
                        }
                      })
                    }
                  })
                } else {
                  openDialog(ConfirmationDialog, {
                    title: 'Activate User',
                    message:
                      'Are you sure you want to activate this user? Clients chargebee subscription will be updated to include new user if they are a billing client.',
                    scary: true,
                    onCancel: dismissDialog,
                    onConfirm: () => {
                      dismissDialog()
                      setFieldValue('user.app_metadata.active', true)
                      submitForm()
                    }
                  })
                }
              }}
            >
              {values.user.app_metadata.active ? 'Deactivate' : 'Activate'}
            </Button>
          </Grid>
        </Grid>
      </FormSection>

      <FormSectionTitle title="Role" />
      <FormSection>
        <Grid item>
          <FieldArray
            name="user.app_metadata.roles"
            render={(arrayHelpers) => (
              <Select
                margin="dense"
                disabled={!isSuperadmin && isViewingSelf}
                value={getHighestRole(arrayHelpers.form.values.user.app_metadata.roles)}
                variant="outlined"
                onChange={(e) => {
                  arrayHelpers.form.setFieldValue('user.app_metadata.roles', [e.target.value])
                }}
              >
                <MenuItem value="support">Support</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="sales">Sales</MenuItem>
                <MenuItem value="crew">Crew</MenuItem>
                <MenuItem value="viewer">Office</MenuItem>
                <MenuItem value="corporate">Corporate</MenuItem>
                {isSuperadmin && values.user.email?.endsWith('@paintscout.com') && (
                  <MenuItem value="superadmin">Superadmin</MenuItem>
                )}
              </Select>
            )}
          />
        </Grid>
      </FormSection>

      <MfaSection userId={values.user.user_id} />

      <FormSectionTitle title="Automation" />
      <FormSection>
        <Grid item>
          <Field
            component={FormikInputField}
            name="user.user_metadata.salesmateId"
            label={`Salesmate ID`}
            toolTip={"The Salesmate ID is the 4 or 5 digit number in a contact's Salesmate URL: /app/contacts/{id}/"}
          />
        </Grid>
      </FormSection>
    </>
  )
}

function getEnrollmentIcon(type: string) {
  switch (type) {
    case 'email':
      return <EmailIcon style={{ fontSize: 40, color: '#999' }} />
    case 'sms':
    case 'phone':
      return <PhoneIcon style={{ fontSize: 40, color: '#999' }} />
    default:
      return <LockIcon style={{ fontSize: 40, color: '#999' }} />
  }
}

function getEnrollmentLabel(type: string) {
  switch (type) {
    case 'email':
      return 'Email'
    case 'sms':
    case 'phone':
      return 'Phone'
    case 'totp':
      return 'Authenticator App'
    case 'push':
      return 'Push Notification'
    default:
      return type
  }
}

function getEnrollmentIdentifier(enrollment: any) {
  if (enrollment.email) return enrollment.email
  if (enrollment.phone_number) return enrollment.phone_number
  if (enrollment.name) return enrollment.name
  return ''
}

function MfaSection({ userId }: { userId: string }) {
  const { openDialog, dismissDialog } = useDialogs()
  const { enqueueSnackbar } = useSnackbar()

  const { data, loading, refetch } = useQuery(GET_MFA_ENROLLMENTS, {
    variables: { userId },
    skip: !userId
  })

  const [resetMfa, { loading: resetting }] = useMutation(RESET_MFA_FOR_USER)

  const enrollments = data?.getMfaEnrollments ?? []
  const hasEnrollments = enrollments.length > 0

  const handleResetMfa = () => {
    openDialog(ConfirmationDialog, {
      title: 'Reset MFA',
      message:
        'Are you sure you want to reset MFA for this user? This will remove all of their enrolled MFA methods. They will need to re-enroll the next time they log in.',
      scary: true,
      onCancel: dismissDialog,
      onConfirm: async () => {
        try {
          await resetMfa({ variables: { userId } })
          await refetch()
          dismissDialog()
          enqueueSnackbar('MFA has been reset for this user.', { variant: 'success' })
        } catch {
          enqueueSnackbar('Failed to reset MFA. Please try again.', { variant: 'error' })
        }
      }
    })
  }

  return (
    <>
      <FormSectionTitle title="Multi-Factor Authentication" />
      <FormSection>
        {loading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : !hasEnrollments ? (
          <Typography variant="body2" color="textSecondary">
            No MFA methods enrolled.
          </Typography>
        ) : (
          <>
            {enrollments.map((enrollment: any, index: number) => (
              <React.Fragment key={enrollment.id}>
                <Box display="flex" alignItems="center" py={2} px={1}>
                  <Box mr={2}>{getEnrollmentIcon(enrollment.type)}</Box>
                  <Box>
                    <Typography variant="body1" style={{ fontWeight: 500 }}>
                      {getEnrollmentIdentifier(enrollment)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Enrolled using {getEnrollmentLabel(enrollment.type)}
                    </Typography>
                  </Box>
                </Box>
                {index < enrollments.length - 1 && <Divider />}
              </React.Fragment>
            ))}
            <Divider />
            <Box
              mt={2}
              py={1.5}
              px={2}
              style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Typography variant="body2">
                User is enrolled in MFA.{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleResetMfa}
                  disabled={resetting}
                  style={{ cursor: 'pointer' }}
                >
                  {resetting ? 'Resetting...' : 'Reset MFA'}
                </Link>
              </Typography>
            </Box>
          </>
        )}
      </FormSection>
    </>
  )
}
